<template>
    <desktop-panel-wrapper show-on-mobile class-list="relative">
        <div class="pb-4">
            <input
                ref="uploaderFullSource" type="file"
                class="hidden" :accept="mimeTypes.join(',')"
                @change="uploaderPicked($event)" />
            <div class="flex space-x-4 mb-2 items-center">
                <button class="flex items-center font-bold text-blue-400 space-x-1" @click="pickFile()">
                    <div>{{ $t('GROUP_CSV') }}</div>
                    <div class="up-arrow-icon h-3 w-3 -mt-0.5" />
                </button>
                <a class="underline" @click.prevent="learnHow">
                    Learn How
                </a>
            </div>

            <div class="font-bold">{{ $t('GROUP_ENTER_CUSTOM') }}</div>
            <div class="text-sm mb-4">{{ $t('GROUP_CHANGE_STYLES_AFTER') }}</div>
            <div class="border-b border-b-gray-300" />

            <!-- How many bottles -->
            <div class="flex w-full justify-between my-4">
                <group-ordering-section
                    :itemNumber="1" :title="$t('GROUP_HOW_MANY_BOTTLES')"
                    :subtitle="$t('GROUP_HOW_MANY_BOTTLES_DESC')" :indicatorValue="numBottles"/>
                <number-selector
                    @open="isNumberSelectorOpen = true"
                    @close="isNumberSelectorOpen = false"
                    :show-quantity-label="false"
                    open-direction="bottom"
                    :value="numBottles" :min="minBottles"
                    :max="maxBottles"
                    @decrease="setBottleCount(numBottles - 1)" @increase="setBottleCount(numBottles + 1)"
                    @select="setBottleCount" />
            </div>

            <!-- Lines / Bottle -->
            <div class="flex w-full justify-between mb-4">
                <group-ordering-section
                    :itemNumber="2"
                    :title="$t('GROUP_HOW_MANY_LINES')"
                    :subtitle="$t('GROUP_HOW_MANY_LINES_DESC', { lines: textPlacements.length })" />
                <number-selector
                    @open="isNumberSelectorOpen = true"
                    @close="isNumberSelectorOpen = false"
                    :show-quantity-label="false"
                    open-direction="bottom"
                    :value="filledTextPlacements.length" :min="minRows"
                    :max="4"
                    @decrease="clearLastTextPlacement()" @increase="listAddText()"
                    @select="setLineCount" />
            </div>

            <!-- Custom Text -->
            <div class="flex w-full">
                <group-ordering-section
                    :itemNumber="3"
                    :title="$t('GROUP_ENTER_TEXT')" />
            </div>
            <div
                v-for="(bottle, bottleIndex) in secondaryBottles"
                :key="bottleIndex" class="py-4">
                <group-bottle-details
                    :key="bottleIndex" :lines="bottle"
                    :bottleIndex="bottleIndex"
                    @update-text="updateText" @remove-bottle="removeBottleAtIndex" />
            </div>

            <button
                class="flex justify-center items-center pb-4 space-x-4"
                @click="setBottleCount(numBottles + 1)">
                <img src="../../assets/image-plus@2x.png" alt="Add bottle" class="h-12 w-12 border border-gray-300 rounded-full">
                <div>{{ $t('GROUP_ADD_BOTTLE') }}</div>
            </button>
        </div>
        <div class="flex flex-col flex-1" />
        <div class="sticky bottom-0 right-0 left-0">
            <div class="bg-white pb-6 -mb-6">
                <primary-button @click="accept" class="mb-2">
                    {{ $t('ACTION_SAVE') }}
                </primary-button>
                <secondary-button @click="cancel">
                    {{ $t('ACTION_CANCEL') }}
                </secondary-button>
            </div>
        </div>
    </desktop-panel-wrapper>
</template>

<script>
    import { mapState } from 'vuex';
    import { useMediaQuery } from '@vueuse/core';
    import ActionButtonsWrapper from '../buttons/ActionButtonsWrapper.vue';
    import SubPanel from '../SubPanel.vue';
    import GroupOrderingSection from '../GroupOrderingSection.vue';
    import NumberSelector from '../NumberSelector.vue';
    import GroupBottleDetails from '../GroupBottleDetails.vue';
    import FullScreenPanel from './FullScreenPanel.vue';
    import * as constants from '../../constants';
    import DesktopPanelWrapper from './desktop/DesktopPanelWrapper.vue';
    import PrimaryButton from '../buttons/PrimaryButton.vue';
    import SecondaryButton from '../buttons/SecondaryButton.vue';

    import designArea from '../../utilities/designArea';
    import { fillEmptyDivs } from '../../utils';

    export default {
        components: {
            FullScreenPanel,
            SubPanel,
            GroupOrderingSection,
            NumberSelector,
            GroupBottleDetails,
            DesktopPanelWrapper,
            ActionButtonsWrapper,
            SecondaryButton,
            PrimaryButton,
        },
        data() {
            return {
                minBottles: 1,
                minRows: 1,
                mimeTypes: ['text/csv'],
                isNumberSelectorOpen: false,
            };
        },
        computed: {
            ...mapState({
                selectedStep: (state) => state.selectedStep,
            }),
            isDesktop() {
                return useMediaQuery(`(min-width: ${constants.DESKTOP_BREAKPOINT}px)`);
            },
            /**
             * Current view controller.
             */
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            /**
             * Return all text placements for the product
             */
            textPlacements() {
                return this.$store.getters.textPlacements;
            },

            /**
             * Gets a list of filled images.
             */
            filledTextPlacements() {
                const textPlacements = this.$store.getters.filledTextPlacements;
                return textPlacements;
            },

            /**
             * Are there any filled text placements
             */
            hasFilledTextPlacements() {
                const textPlacements = this.filledTextPlacements;
                return textPlacements && textPlacements.length > 0;
            },

            /**
             * Number of filled text placements.
             */
            filledTextPlacementsCount() {
                return (this.filledTextPlacements || []).length;
            },

            /**
             * Gets a list of filled placements _only_, without components.
             */
            editablePlacements() {
                return (this.filledTextPlacements || []).map((p) => p.placement);
            },

            /**
             * Get secondary bottles.
             */
            secondaryBottles() {
                return this.$store.state.customizer.secondaryBottles;
            },

            /**
             * Are there any filled text placements
             */
            numBottles() {
                return this.secondaryBottles.length;
            },

            /**
             * Maximum number of bottles of selected color.
             */
            maxBottles() {
                // Same as the current production limit.
                // return Math.min(150, this.$store.getters.availableQuantity);
                return this.$store.getters.availableQuantity;
            },

            /**
             * Gets a list of free images.
             */
            emptyTextPlacements() {
                return this.$store.getters.emptyTextPlacements;
            },

            /**
             * Are there any empty text placements?
             */
            hasEmptyTextPlacements() {
                return this.emptyTextPlacements && this.emptyTextPlacements.length > 0;
            },

            /**
             * Currently selected view.
             */
            currentView() {
                return this.$store.state.customizer.currentView;
            },

            /**
             * Currently selected bottle.
             */
            selectedBottle() {
                return this.$store.state.customizer.selectedBottle;
            },

            /**
             * Gets the selected bottle index.
             */
            selectedBottleIndex() {
                const bottle = this.$store.state.customizer.selectedBottle;
                const bottles = this.$store.state.customizer.secondaryBottles;

                if (bottle && bottles) {
                    return bottles.indexOf(bottle);
                }

                return -1;
            },
        },
        watch: {
            filledTextPlacementsCount() {
            // this.$store.dispatch('editPlacement', this.editablePlacements);
            },
        },
        mounted() {
            // When the component is loaded, capture the current state of the view controller and save it in case
            // we need to revert to the previous state and toss away all customizations.
            //
            // At any given time there should be only one module that can do the revert.
            this.captureController();

            // If there are no personalization placements, make one.
            if (this.filledTextPlacements && this.filledTextPlacements.length === 0) {
                this.listAddText();
            }
            fillEmptyDivs();

        // Automatically enter the edit mode for all filled text placements.
        // this.$store.dispatch('lockSwipe', true);
        // this.$store.dispatch('editPlacement', this.editablePlacements);
        },
        beforeDestroy() {
        // Reenable swiping.
        // this.$store.dispatch('lockSwipe', false);
        // this.$store.dispatch('editPlacement', null);
        },
        methods: {
            /**
             * Captures the current state and active controller so we could cancel changes if necessary.
             */
            captureController() {
                if (this.viewController) {
                    this.revertController = this.viewController.clone({ excludeEvents: false });
                }
            },

            /**
             * Accept changes.
             */
            accept() {
                // Go back to the main screen.
                this.$store.dispatch('selectStep', null);
            },

            /**
             * Go back.
             */
            cancel() {
                this.$store.dispatch('showGenericConfirm', {
                    title: this.$t('CANCEL_CONFIRMATION'),
                    description: this.$t('CANCEL_CONFIRMATION_BODY'),
                    cancelAction: this.$t('CANCEL_CONFIRMATION_CANCEL'),
                    confirmAction: this.$t('CANCEL_CONFIRMATION_OK'),
                    confirmationCallback: () => {
                        // Revert back to the state of the controller as was captured when the component was mounted.
                        this.$store.dispatch('showController', this.revertController);
                        // Go back to the main screen.
                        this.$store.dispatch('selectStep', null);
                    },
                });
            },

            /**
             * Update the text for the primary bottle / text placements
             */
            updateText(bottleIndex, lineIndex, newValue) {
                // Access local data to make the change, account for index offset
                this.$store.dispatch('updateSecondaryBottle', {
                    bottle: bottleIndex,
                    line: lineIndex,
                    text: newValue,
                });

                if (bottleIndex === this.selectedBottleIndex) {
                    // Placement-driven bottle
                    const placement = this.filledTextPlacements[lineIndex];
                    const placementCode = placement.placement.code;

                    this.viewController.updatePersonalization(placementCode, newValue);
                }

            // TODO: need to add this logic in to account for allowed chars etc.

            // .then((result) => {
            // console.log(result);
            // if (result.accepted) {
            //     if (result.as) {
            //         this.selectedText = result.as;
            //     }
            // } else if (result.lastValid) {
            //     this.selectedText = result.lastValid;
            // }
            // });
            },

            /**
             * Update the number of bottles in the group ordering
             */
            setBottleCount(newBottleCount) {
                if (newBottleCount > this.maxBottles || newBottleCount < this.minBottles) {
                    return;
                }

                const difference = newBottleCount - this.secondaryBottles.length;

                if (difference < 0) {
                    // Remove bottles, account for text placement bottle
                    this.$store.dispatch('setSecondaryBottles', this.secondaryBottles.splice(0, newBottleCount));

                    // Removed the selected bottle...
                    if (this.secondaryBottles.indexOf(this.selectedBottle) === -1) {
                        this.$store.dispatch('setSelectedBottle', this.secondaryBottles[0]);
                    }
                } else {
                    // Add new bottles
                    const newBottles = [...this.secondaryBottles];
                    const numberOfLines = this.filledTextPlacements.length;

                    const emptyObject = [];
                    for (let i = 0; i < numberOfLines; i += 1) {
                        emptyObject.push('');
                    }

                    for (let i = 0; i < difference; i += 1) {
                        newBottles.push([...emptyObject]);
                    }

                    this.$store.dispatch('setSecondaryBottles', newBottles);
                }
            },

            /**
             * Update the number of lines per bottle
             */
            setLineCount(newLineCount) {
                // Determine difference
                const { filledTextPlacementsCount } = this;
                const lineCountDiff = newLineCount - filledTextPlacementsCount;
                // Add lines - if needed
                for (let i = 0; i < lineCountDiff; i += 1) {
                    this.listAddText();
                }
                // Remove lines - if needed
                for (let i = 0; i > lineCountDiff; i -= 1) {
                    this.clearLastTextPlacement();
                }
            },

            /**
             * Remove a sepcific bottle, adjust for offet
             */
            removeBottleAtIndex(bottleIndex) {
                const newBottles = [...this.secondaryBottles];

                newBottles.splice(bottleIndex, 1);

                this.$store.dispatch('setSecondaryBottles', newBottles);

                // Removed the selected bottle...
                if (this.secondaryBottles.indexOf(this.selectedBottle) === -1) {
                    this.$store.dispatch('setSelectedBottle', this.secondaryBottles[0]);
                }
            },

            /**
             * Begins adding a new text.
             */
            listAddText(initialText = '', shouldTopAlignBottles = false) {
                if (this.emptyTextPlacements.length > 0) {
                    this.$store.dispatch('validateEditableViews');

                    const { placement } = this.emptyTextPlacements[0];

                    this.$store.dispatch('takeEmptyPlacement', placement);

                    // ... and center manually in the current view.
                    if (this.currentView) {
                        const { view } = this.currentView;

                        if (view.custom) {
                            const {
                                left, right, top, bottom,
                            } = designArea(this.$store.state.customizer, view.custom['v-design-area']);

                            const width = +1;
                            const height = +1;

                            if (left != null && right != null && top != null && bottom != null) {
                                let yOffset = null;

                                this.filledTextPlacements.forEach((p) => {
                                    if (p.component.custom['personalization-y'] != null) {
                                        if (yOffset == null) {
                                            yOffset = +p.component.custom['personalization-y'];
                                        } else {
                                            yOffset = Math.max(+p.component.custom['personalization-y'], yOffset);
                                        }
                                    }
                                });

                                if (yOffset == null) {
                                    yOffset = top + 60;
                                } else {
                                    yOffset += this.viewController.blueprint.custom['v-group-y-offset']
                                        ? (+this.viewController.blueprint.custom['v-group-y-offset'])
                                        : 100;
                                }

                                const group = this.viewController.blueprint.custom[`v-group-${view.custom['v-design-area']}`];

                                this.$store.dispatch('setGroup', { placement: placement.code, group });

                                this.viewController.commitCustomAttributes(placement.code, {
                                    'v-design-on': view.custom['v-design-area'],
                                    mask: view.custom['v-design-mask'],
                                    'personalization-x': ((left + right) * 0.5) - (width * 0.5),
                                    'personalization-y': yOffset,
                                    'personalization-rotate': 0,
                                    'personalization-scale': 1,
                                    'personalization-text': initialText || '',
                                    'personalization-filled': 'true',
                                    'personalization-width': '1',
                                    'personalization-height': '1',
                                    // Even though this might be added on secondary bottles, let that bottle define the dimensions.
                                    // They would be overridden by the first bottle anyway.
                                    'personalization-size-fit-min': 'none',
                                }, {
                                    propagate: true,
                                });

                                // Add lines to secondary bottles
                                if (this.secondaryBottles.length > 0) {
                                    const newSecondaryBottles = this.secondaryBottles.map((secondaryBottle) => {
                                        const newBottle = secondaryBottle;
                                        secondaryBottle.push('');
                                        return newBottle;
                                    });

                                    this.$store.dispatch('setSecondaryBottles', newSecondaryBottles);
                                }
                            }
                        }
                    }
                }
            },

            /**
             * Remove the last text placement in the list of active placements.
             */
            clearLastTextPlacement() {
                const filledPlacements = this.filledTextPlacements;
                if (filledPlacements.length > 0) {
                    const lastFilledPlacement = filledPlacements[filledPlacements.length - 1];
                    this.$store.dispatch('clearPlacement', lastFilledPlacement);

                    // Remove lines from secondary bottles
                    const newSecondaryBottles = this.secondaryBottles.map((secondaryBottle) => {
                        const newBottle = [...secondaryBottle];
                        newBottle.pop();
                        return newBottle;
                    });

                    this.$store.dispatch('setSecondaryBottles', newSecondaryBottles);
                }
            },

            /**
             * Begin the process of uploading a csv
             */
            beginCSVUpload() {
            },

            /**
             * Triggers file selection.
             */
            pickFile() {
                this.$refs.uploaderFullSource.click();
            },

            /**
             * Picked a file, try and read first file
             */
            uploaderPicked() {
                const uploadedFiles = this.$refs.uploaderFullSource.files;
                if (uploadedFiles.length > 0) {
                    const reader = new FileReader();
                    reader.onload = this.handleFileLoad;
                    reader.readAsText(uploadedFiles[0]);
                }
            },

            /**
             * Parse CSV test.
             * as found in https://stackoverflow.com/questions/8493195/how-can-i-parse-a-csv-string-with-javascript-which-contains-comma-in-data
             */
            csvToArray(text) {
                let p = '';
                let row = [''];
                const ret = [row];
                let i = 0;
                let r = 0;
                let s = !0;
                let l;
                // eslint-disable-next-line
            for (l of text) {
                    if (l === '"') {
                        if (s && l === p) { row[i] += l; }
                        s = !s;
                    } else if (l === ',' && s) {
                        i += 1;
                        l = '';
                        row[i] = '';
                    } else if (l === '\n' && s) {
                        if (p === '\r') {
                            row[i] = row[i].slice(0, -1);
                        }
                        r += 1;
                        l = '';
                        row = [''];
                        ret[r] = [''];
                        i = 0;
                    } else {
                        row[i] += l;
                    }
                    p = l;
                }
                return ret;
            },

            /**
             * Handle reading a file from file reader.
             */
            handleFileLoad(event) {
                // Get CSV Data
                const fileText = event.target.result;

                this.$refs.uploaderFullSource.value = null;

                const bottles = fileText.split('\n').filter((line) => line.trim().length > 0).map((line) => {
                    const parsed = this.csvToArray(line);

                    const values = parsed.length > 0 ? parsed[0] : [];

                    return values.map((v) => v.trim());
                });

                const firstBottleLines = bottles[0];

                const linesPerBottle = Math.min(firstBottleLines.length, this.textPlacements.length);

                // First bottle, clear and set placements
                [...this.filledTextPlacements].forEach((textPlacement) => {
                    this.$store.dispatch('clearPlacement', textPlacement);
                });

                for (let i = 0; i < linesPerBottle; i += 1) {
                    this.listAddText(firstBottleLines[i], linesPerBottle > 3);
                }

                // Secondary bottles
                const newBottles = [];

                let totalBottles = bottles.length;

                if (totalBottles > this.maxBottles) {
                    totalBottles = this.maxBottles;

                    this.$store.dispatch('showGenericNotify', {
                        title: this.$t('GROUP_IMPORT_WARNING'),
                        description: this.$t('GROUP_IMPORT_WARNING_BODY'),
                        action: this.$t('ACTION_CONTINUE'),
                    });
                }

                for (let i = 0; i < totalBottles; i += 1) {
                    const newBottle = [];

                    const lines = bottles[i];

                    for (let j = 0; j < firstBottleLines.length; j += 1) {
                        newBottle.push(lines[j]);
                    }

                    newBottles.push(newBottle);
                }

                this.$store.dispatch('setSecondaryBottles', newBottles);
                this.$store.dispatch('setSelectedBottle', newBottles[0]);
            },

            learnHow() {
                this.$store.dispatch('showGenericNotify', {
                    description: this.$t('GROUP_LEARN_HOW'),
                    action: this.$t('ACTION_CONTINUE'),
                });
            },
        },
    };
</script>
